//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';
import { PojoMap } from 'pojo-maps';
import { U } from 'ts-toolbelt';

import * as showroomApi from '@apis/showroom/showroomApi';
import * as urlTrackingApi from '@apis/url-tracking/urlTrackingApi';
import { SHOPPABLE_INSTAGRAM_TYPES } from '@instagram-account/instagram-account-constants/instagram-account-constants';
import { InstagramPostDetailsValidationError } from '@instagram-account/redux/post-details/instagramPostDetailsModel';
import {
  IInstagramPostDetailsStoryClip,
  IInstagramStoryGroupsPostDetailsState,
} from '@instagram-account/redux/story-group-post-details/instagramStoryGroupPostDetailsModel';
import {
  defaultStoryClip,
  getSwipeUpLinkDataFromStoryClips,
  isCuralateShortLink,
  IStoryGroupSwipeUpLinkData,
  validateStoryClip,
} from '@instagram-account/redux/story-group-post-details/instagramStoryGroupPostDetailsUtils';
import { IShowroomData } from '@legacy-components/common/universal-image-actions/models/events';
import Cuid from '@models/Cuid';
import {
  IInstagramStoryGroupClipCustomization,
  InstagramStoryGroupClipCustomizationType,
} from '@models/IScheduledInstagramStoryGroup';
import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { createThunk } from '@redux/createThunk';
import { getClientId } from '@redux/selectors/getClientId';
import { SliceNames } from '@redux/SliceNames';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isValidUrlWithProtocol } from '@utils/urlValidation';
import { isDefined } from '@utils/filterPredicates';
import { move } from '@utils/arrayUtils';
import * as scheduleMediaActions from '@redux/schedule-media/scheduleMediaSlice';
import { getScheduleMediaMap } from '@redux/schedule-media/getScheduleMediaMap';
import {
  IInstagramStoryGroupAutomaticPostConfig,
  IInstagramStoryGroupPostConfig,
} from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import { getCurrentStoryClips } from '@instagram-account/redux/story-group-post-details/selectors/getCurrentStoryClips';
import { getPublishingStrategyAndInfoText } from '@instagram-account/redux/post-details/selectors/get-publishing-strategy/getPublishingStrategyAndInfoText';
import { IDraftInstagramPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import { getInstagramStandardPostDetails } from '@instagram-account/redux/schedule/instagramScheduleUtils';

const initialState: IInstagramStoryGroupsPostDetailsState = {
  currentStoryGroupPostDetails: {
    storyClips: [defaultStoryClip],
  },
  initialStoryGroupPostDetails: {
    storyClips: [defaultStoryClip],
  },
  currentClipIndex: 0,
  flags: {
    isFetchingSelectedMedia: false,
    isGettingShowroomData: false,
    isFetchingAugmentedSwipeUpLink: false,
    isFetchingFullSwipeUpLink: false,
  },
};

function getCurrentClip(state: IInstagramStoryGroupsPostDetailsState) {
  return state.currentStoryGroupPostDetails.storyClips[state.currentClipIndex];
}

function getCurrentSwipeUpCustomization(
  state: IInstagramStoryGroupsPostDetailsState,
) {
  return getCurrentClip(state).customizations[0];
}

const { reducer: InstagramStoryGroupPostDetailsReducer, actions } = createSlice(
  {
    name: SliceNames.InstagramAccountStoryGroupsPostDetails,
    initialState,
    reducers: {
      setInitialStoryClips(
        state,
        {
          payload: storyClips,
        }: PayloadAction<
          Array<{
            cuid?: Cuid;
            customizations: IInstagramStoryGroupClipCustomization[];
          }>
        >,
      ) {
        const initialStoryClips = storyClips.map((clip) => {
          return {
            ...clip,
            validationErrors: defaultStoryClip.validationErrors,
            errors: defaultStoryClip.errors,
          };
        });

        state.currentStoryGroupPostDetails.storyClips = initialStoryClips;
        state.initialStoryGroupPostDetails.storyClips = initialStoryClips;
      },
      addStoryClips(
        state,
        {
          payload: storyClips,
        }: PayloadAction<IInstagramPostDetailsStoryClip[]>,
      ) {
        state.currentStoryGroupPostDetails.storyClips.splice(
          state.currentClipIndex + 1,
          0,
          ...storyClips,
        );
      },
      removeStoryClip(state, { payload: clipIndex }: PayloadAction<number>) {
        state.currentStoryGroupPostDetails.storyClips.splice(clipIndex, 1);

        const numberOfClips =
          state.currentStoryGroupPostDetails.storyClips.length;

        // Reset state if removing the last clip
        if (numberOfClips === 0) {
          state.currentStoryGroupPostDetails.storyClips =
            initialState.currentStoryGroupPostDetails.storyClips;
          state.currentClipIndex = initialState.currentClipIndex;
          // Adjust current clip index if removing clip earlier in list or last clip
        } else if (
          clipIndex < state.currentClipIndex ||
          state.currentClipIndex >= numberOfClips
        ) {
          state.currentClipIndex = state.currentClipIndex - 1;
        }
      },
      updateStoryClipPosition(
        state,
        {
          payload: { currIndex, newIndex },
        }: PayloadAction<{
          currIndex: number;
          newIndex: number;
        }>,
      ) {
        state.currentStoryGroupPostDetails.storyClips = move(
          state.currentStoryGroupPostDetails.storyClips,
          currIndex,
          newIndex,
        );
        state.currentClipIndex = newIndex;
      },
      addNewEmptyClip(state) {
        state.currentStoryGroupPostDetails.storyClips.push(defaultStoryClip);
        state.currentClipIndex =
          state.currentStoryGroupPostDetails.storyClips.length - 1;
      },
      setClipCuid(
        state,
        {
          payload: { clipIndex, cuid },
        }: PayloadAction<{
          clipIndex: number;
          cuid: Cuid;
        }>,
      ) {
        const clip = state.currentStoryGroupPostDetails.storyClips[clipIndex];
        clip.cuid = cuid;

        clip.validationErrors = PojoSet.remove(
          clip.validationErrors,
          InstagramPostDetailsValidationError.NoMediaStoryClip,
        );
      },
      setClipCustomizationValue(
        state,
        {
          payload: { customizationIndex, value },
        }: PayloadAction<{
          customizationIndex: number;
          value: string;
        }>,
      ) {
        getCurrentClip(state).customizations[customizationIndex].value = value;
      },
      addClipCustomization(
        state,
        {
          payload: customizationType,
        }: PayloadAction<InstagramStoryGroupClipCustomizationType>,
      ) {
        getCurrentClip(state).customizations.push({ customizationType });
      },
      removeClipCustomization(
        state,
        { payload: customizationIndex }: PayloadAction<number>,
      ) {
        getCurrentClip(state).customizations.splice(customizationIndex, 1);
      },
      removeClipCustomizationsAndShowroomData(
        state,
        {
          payload: { clipIndex },
        }: PayloadAction<{
          clipIndex: number;
        }>,
      ) {
        const clip = state.currentStoryGroupPostDetails.storyClips[clipIndex];
        if (isDefined(clip)) {
          clip.customizations = defaultStoryClip.customizations;
          clip.validationErrors = defaultStoryClip.validationErrors;
          clip.errors = defaultStoryClip.validationErrors;
          clip.fullSwipeUpLink = undefined;
          clip.showroomShortLink = undefined;
          clip.showroomPreviewUrl = undefined;
          clip.hasAugmentedSwipeUpLink = undefined;
          clip.hasUpdatedShowroomToContextual = undefined;
        }
      },
      setCurrentClipIndex(state, { payload: index }: PayloadAction<number>) {
        state.currentClipIndex = index;
      },
      setClipValidationErrors(
        state,
        {
          payload: { clipIndex, validationErrors },
        }: PayloadAction<{
          clipIndex: number;
          validationErrors: PojoSet<InstagramPostDetailsValidationError>;
        }>,
      ) {
        state.currentStoryGroupPostDetails.storyClips[
          clipIndex
        ].validationErrors = validationErrors;
      },
      setAllValidationErrors(
        state,
        {
          payload: errors,
        }: PayloadAction<Array<PojoSet<InstagramPostDetailsValidationError>>>,
      ) {
        state.currentStoryGroupPostDetails.storyClips.map(
          (c, index) => (c.validationErrors = errors[index]),
        );
      },
      requestSelectedMedia(state) {
        state.flags.isFetchingSelectedMedia = true;
      },
      receiveSelectedMediaSucceeded(state) {
        state.flags.isFetchingSelectedMedia = false;
      },
      receiveSelectedMediaFailed(state) {
        state.flags.isFetchingSelectedMedia = false;
      },
      setFullSwipeUpLink(
        state,
        { payload: swipeUpLink }: PayloadAction<string>,
      ) {
        const currentClip = getCurrentClip(state);
        currentClip.fullSwipeUpLink = swipeUpLink;

        const currentSwipeUpLink = getCurrentSwipeUpCustomization(state);
        const hasShortenedSwipeUpLink =
          currentSwipeUpLink.value &&
          isCuralateShortLink(currentSwipeUpLink.value);
        if (!hasShortenedSwipeUpLink || !swipeUpLink) {
          currentSwipeUpLink.value = swipeUpLink;
          currentClip.hasAugmentedSwipeUpLink = false;
        }

        currentClip.validationErrors = PojoSet.remove(
          currentClip.validationErrors,
          InstagramPostDetailsValidationError.InvalidUrl,
        );
        currentClip.errors.fetchAugmentedLinkError = undefined;
      },
      requestShowroomData(state) {
        state.flags.isGettingShowroomData = true;
      },
      receiveShowroomDataSucceeded(
        state,
        {
          payload: { shortLink, previewUrl, clipIndex, hasUpdatedToContextual },
        }: PayloadAction<{
          shortLink: string;
          previewUrl: string;
          clipIndex?: number;
          hasUpdatedToContextual?: boolean;
        }>,
      ) {
        const currentClip = isDefined(clipIndex)
          ? state.currentStoryGroupPostDetails.storyClips[clipIndex]
          : getCurrentClip(state);
        const swipeUpLink = currentClip.customizations[0];
        swipeUpLink.value = shortLink;
        currentClip.showroomShortLink = shortLink;
        currentClip.showroomPreviewUrl = previewUrl;
        currentClip.hasAugmentedSwipeUpLink = false;
        currentClip.hasUpdatedShowroomToContextual = hasUpdatedToContextual;
        state.flags.isGettingShowroomData = false;
      },
      receiveShowroomDataFailed(state) {
        state.flags.isGettingShowroomData = false;
      },
      clearShowroomData(state) {
        const currentClip = getCurrentClip(state);
        currentClip.showroomPreviewUrl = undefined;
        currentClip.showroomShortLink = '';
        currentClip.fullSwipeUpLink = '';
        currentClip.hasUpdatedShowroomToContextual = false;
        getCurrentSwipeUpCustomization(state).value = '';
      },
      requestAugmentedSwipeUpLink(state) {
        state.flags.isFetchingAugmentedSwipeUpLink = true;
      },
      receiveAugmentedSwipeUpLinkSucceeded(
        state,
        {
          payload: { shortLink, clipIndex },
        }: PayloadAction<{ shortLink: string; clipIndex?: number }>,
      ) {
        const currentClip = isDefined(clipIndex)
          ? state.currentStoryGroupPostDetails.storyClips[clipIndex]
          : getCurrentClip(state);
        currentClip.hasAugmentedSwipeUpLink = true;
        const swipeUpLink = currentClip.customizations[0];
        swipeUpLink.value = shortLink;
        state.flags.isFetchingAugmentedSwipeUpLink = false;
      },
      receiveAugmentedSwipeUpLinkFailed(
        state,
        {
          payload: { error, clipIndex },
        }: PayloadAction<{ error: string; clipIndex?: number }>,
      ) {
        const currentClip = isDefined(clipIndex)
          ? state.currentStoryGroupPostDetails.storyClips[clipIndex]
          : getCurrentClip(state);
        state.flags.isFetchingAugmentedSwipeUpLink = false;
        currentClip.errors.fetchAugmentedLinkError = error;
      },
      requestFullSwipeUpLinks(state) {
        state.flags.isFetchingFullSwipeUpLink = true;
      },
      receiveFullSwipeUpLinksSucceeded(
        state,
        { payload: fullSwipeUpLinks }: PayloadAction<Record<string, string>>,
      ) {
        state.currentStoryGroupPostDetails.storyClips.map((clip) => {
          const swipeUpShortLink = clip.customizations[0].value;
          if (swipeUpShortLink && fullSwipeUpLinks[swipeUpShortLink]) {
            clip.fullSwipeUpLink = fullSwipeUpLinks[swipeUpShortLink];

            if (isCuralateShortLink(swipeUpShortLink)) {
              clip.hasAugmentedSwipeUpLink = true;
            }
          }
        });
        state.flags.isFetchingFullSwipeUpLink = false;
      },
      receiveFullSwipeUpLinksFailed(
        state,
        {
          payload: { fallbackUrls, fetchFullLinkError },
        }: PayloadAction<{
          fallbackUrls: string[];
          fetchFullLinkError: string;
        }>,
      ) {
        state.currentStoryGroupPostDetails.storyClips.map((clip, index) => {
          clip.fullSwipeUpLink = fallbackUrls[index];
          clip.errors.fetchFullLinkError = fetchFullLinkError;
        });
        state.flags.isFetchingFullSwipeUpLink = false;
      },
      resetStoryGroupPostDetails: () => initialState,
    },
  },
);

export const {
  addClipCustomization,
  addNewEmptyClip,
  clearShowroomData,
  removeClipCustomization,
  removeClipCustomizationsAndShowroomData,
  removeStoryClip,
  setClipCuid,
  resetStoryGroupPostDetails,
  setClipCustomizationValue,
  setCurrentClipIndex,
  setInitialStoryClips,
  setFullSwipeUpLink,
  updateStoryClipPosition,

  addStoryClips: _testOnlyAddStoryClips,

  receiveShowroomDataSucceeded: _testOnlyReceiveShowroomDataSuceeded,
  receiveShowroomDataFailed: _testOnlyReceiveShowroomDataFailed,
  requestFullSwipeUpLinks: _testOnlyRequestFullSwipeUpLinks,
} = actions;

/**
 * Set up initial story clips for creating a scheduled story group with the selected cuid.
 */
export const setUpCreateStoryGroup = createThunk(
  (cuids: Cuid[]) => (dispatch) => {
    const storyClips = cuids.map((cuid) => ({
      cuid,
      ...defaultStoryClip,
    }));

    dispatch(actions.setInitialStoryClips(storyClips));
  },
);

/**
 * Set up initial story clips for editing a scheduled story group.
 * Fetches the full (destination) urls for any swipe up link customizations.
 */
export const setUpEditStoryGroup = createThunk(
  (
    config: U.Strict<
      IInstagramStoryGroupPostConfig | IInstagramStoryGroupAutomaticPostConfig
    >,
  ) =>
    async (dispatch) => {
      const { storyClips, cuids } = config;

      const storyGroupClips = isDefined(storyClips)
        ? storyClips.map((clip) => {
            return {
              cuid: clip.cuid,
              customizations: clip.customizations.map((customization) => {
                return {
                  value: customization.value,
                  customizationType:
                    InstagramStoryGroupClipCustomizationType[
                      customization.customizationType
                    ],
                };
              }),
            };
          })
        : cuids.map((cuid) => {
            return {
              cuid: cuid,
              customizations: defaultStoryClip.customizations,
            };
          });

      dispatch(actions.setInitialStoryClips(storyGroupClips));

      const swipeUpLinksData =
        getSwipeUpLinkDataFromStoryClips(storyGroupClips);

      if (swipeUpLinksData.length > 0) {
        await dispatch(setupSwipeUpLinks(swipeUpLinksData));
      }
    },
);

/**
 * Set up initial story clips for a draft item. Fetch full urls for Curalate short links.
 */
export const setUpEditDraftStoryGroup = createThunk(
  (draft: IDraftInstagramPost) => async (dispatch) => {
    const { storyClips } = getInstagramStandardPostDetails(draft);
    if (storyClips) {
      dispatch(actions.setInitialStoryClips(storyClips));

      const swipeUpLinksData = getSwipeUpLinkDataFromStoryClips(storyClips);

      if (swipeUpLinksData.length === 0) {
        return;
      }

      await dispatch(setupSwipeUpLinks(swipeUpLinksData));
    }
  },
);

export const fetchAndSetSelectedMedia = createThunk(
  (items: IContentLibraryItem[]) => async (dispatch, getState) => {
    const clientId = getClientId(getState());
    const newCuids = items.map((item) => Cuid.build(item, clientId));
    const dedupedCuids = Array.from(new Set(newCuids));
    const { currentClipIndex } = getState().instagram.storyGroupPostDetails;

    dispatch(actions.requestSelectedMedia());

    try {
      await dispatch(scheduleMediaActions.getMediaItemsByCuid(dedupedCuids));
      dispatch(actions.receiveSelectedMediaSucceeded());
    } catch (e) {
      dispatch(actions.receiveSelectedMediaFailed());
    }

    const [firstCuid, ...remainingCuids] = newCuids;
    dispatch(
      actions.setClipCuid({ clipIndex: currentClipIndex, cuid: firstCuid }),
    );

    // Augment swipe-up link now that media has been selected
    void dispatch(validateAndAugmentSwipeUpLink());

    if (remainingCuids.length > 0) {
      const newClips = remainingCuids.map((cuid) => {
        return {
          cuid,
          ...defaultStoryClip,
        };
      });
      dispatch(actions.addStoryClips(newClips));
    }
  },
);

/**
 * Gets the short link for the provided contextual showroom data that
 * was generated during the UIA Showroom Workflow.
 */
export const getShortLinkForContextualShowroomData = createThunk(
  (showroomData: IShowroomData, sourceId: string) =>
    async (dispatch): Promise<boolean> => {
      dispatch(actions.requestShowroomData());
      try {
        const { shortLink } = await showroomApi.getShowroomShortLink({
          implId: showroomData.impl.id,
          name: showroomData.impl.name,
          sourceId,
          sourceType: 'item',
          instanceId: showroomData.instances?.[0]?.instanceId,
        });
        if (shortLink) {
          dispatch(
            actions.receiveShowroomDataSucceeded({
              shortLink,
              previewUrl: showroomData.previewUrl,
            }),
          );
        }
        return true;
      } catch (e) {
        dispatch(actions.receiveShowroomDataFailed());
        return false;
      }
    },
);

/**
 * Validates the current story clips. Returns true if there are no validation errors.
 */
export const validateStoryClips = createThunk(
  () =>
    (dispatch, getState): boolean => {
      const { storyClips } =
        getState().instagram.storyGroupPostDetails.currentStoryGroupPostDetails;
      const scheduleMedia = getScheduleMediaMap(getState());

      const { validatedPublishingStrategy } =
        getPublishingStrategyAndInfoText(getState());

      let allValidationErrors: Array<
        PojoSet<InstagramPostDetailsValidationError>
      > = [];
      storyClips.map((clip) => {
        const associatedNetworkItem =
          clip.cuid && PojoMap.get(scheduleMedia, clip.cuid)?.networkItem;
        const validationErrors = validateStoryClip(
          clip,
          validatedPublishingStrategy,
          associatedNetworkItem,
        );
        allValidationErrors = [...allValidationErrors, validationErrors];
      });

      dispatch(actions.setAllValidationErrors(allValidationErrors));

      return !allValidationErrors.some((e) => PojoSet.size(e) > 0);
    },
);

/**
 * Validates the current swipe up link and attempts to fetch an augmented url if the clip has a cuid.
 * If the current swipe up link looks like it could be a showroom link, attempt to fetch the contextual
 * showroom link instead of augmenting it.
 */
export const validateAndAugmentSwipeUpLink = createThunk(
  (clipIndex?: number) => (dispatch, getState) => {
    const {
      currentStoryGroupPostDetails: { storyClips },
    } = getState().instagram.storyGroupPostDetails;

    const currentClipIndex =
      clipIndex ?? getState().instagram.storyGroupPostDetails.currentClipIndex;

    const currentClip = storyClips[currentClipIndex];
    const fullSwipeUpLink = currentClip.fullSwipeUpLink;

    if (!fullSwipeUpLink) {
      return;
    }

    const hasValidUrl = isValidUrlWithProtocol(fullSwipeUpLink);
    if (!hasValidUrl) {
      dispatch(
        actions.setClipValidationErrors({
          clipIndex: currentClipIndex,
          validationErrors: PojoSet.add(
            currentClip.validationErrors,
            InstagramPostDetailsValidationError.InvalidUrl,
          ),
        }),
      );
    } else if (currentClip.cuid) {
      if (isCuralateShortLink(fullSwipeUpLink)) {
        void dispatch(
          getContextualShowroomLinkFromUrl(
            fullSwipeUpLink,
            currentClip.cuid,
            currentClipIndex,
          ),
        );
      } else {
        void dispatch(
          fetchAugmentedSwipeUpLink(
            fullSwipeUpLink,
            currentClip.cuid,
            currentClipIndex,
          ),
        );
      }
    }
  },
);

/**
 * Attempt to retrieve a contextual showroom shortlink from the given url. If this fails, indicating the url
 * is not a showroom link, fall back to creating an augmented shortlink using the url tracking service.
 */
export const getContextualShowroomLinkFromUrl = createThunk(
  (url: string, cuid: Cuid, clipIndex: number) => async (dispatch) => {
    dispatch(actions.requestShowroomData());
    try {
      const { shortLink, previewUrl } = await showroomApi.getContextualShowroom(
        {
          sourceId: cuid,
          implementationType: SHOPPABLE_INSTAGRAM_TYPES.IG_STORY,
          showroomUrl: url,
        },
      );

      dispatch(
        actions.receiveShowroomDataSucceeded({
          shortLink,
          previewUrl,
          clipIndex,
          hasUpdatedToContextual: true,
        }),
      );
    } catch (e) {
      // If an error was thrown, the link is not a showroom link and we need to augment it with tracking params
      void dispatch(fetchAugmentedSwipeUpLink(url, cuid, clipIndex));
    }
  },
);

/**
 * Fetches a story swipe up link that has been augmented with tracking params and shortened
 */
const fetchAugmentedSwipeUpLink = createThunk(
  (url: string, cuid: Cuid, clipIndex?: number) => async (dispatch) => {
    dispatch(actions.requestAugmentedSwipeUpLink());
    try {
      const augmentedUrl = await urlTrackingApi.getAugmentedSwipeUpLink({
        url,
        cuid,
      });
      dispatch(
        actions.receiveAugmentedSwipeUpLinkSucceeded({
          shortLink: augmentedUrl,
          clipIndex,
        }),
      );
    } catch (_e) {
      dispatch(
        actions.receiveAugmentedSwipeUpLinkFailed({
          error:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          clipIndex,
        }),
      );
    }
  },
);

/**
 * Sets up the correct state for all swipe up links in the story group. If the clip has media, we first
 * attempt to fetch showroom data. If the clip has no media or the link is not a showroom, fetch and set
 * the full swipe up link instead.
 */
export const setupSwipeUpLinks = createThunk(
  (swipeUpLinkData: IStoryGroupSwipeUpLinkData[]) => async (dispatch) => {
    let nonShowroomLinks: string[] = [];
    await Promise.all(
      swipeUpLinkData.map(async (d) => {
        if (!d.cuid) {
          nonShowroomLinks = [...nonShowroomLinks, d.url];
        } else {
          dispatch(actions.requestShowroomData());
          try {
            const { shortLink, previewUrl } =
              await showroomApi.getContextualShowroom({
                sourceId: d.cuid,
                implementationType: SHOPPABLE_INSTAGRAM_TYPES.IG_STORY,
                showroomUrl: d.url,
              });
            dispatch(
              actions.receiveShowroomDataSucceeded({
                shortLink,
                previewUrl,
                clipIndex: d.clipIndex,
              }),
            );
          } catch {
            dispatch(actions.receiveShowroomDataFailed());
            nonShowroomLinks = [...nonShowroomLinks, d.url];
          }
        }
      }),
    );

    if (nonShowroomLinks.length > 0) {
      await dispatch(fetchFullSwipeUpLinks(nonShowroomLinks));
    }
  },
);

/**
 * Attempts to fetch the full (unfurled) destination URL from the given list of urls.
 * If we fail to fetch the full urls, just return the given urls.
 */
export const fetchFullSwipeUpLinks = createThunk(
  (urls: string[]) => async (dispatch) => {
    dispatch(actions.requestFullSwipeUpLinks());

    try {
      const destinationUrls =
        await urlTrackingApi.getDestinationUrlsFromShortlinks({ links: urls });
      dispatch(actions.receiveFullSwipeUpLinksSucceeded(destinationUrls));
    } catch (_e) {
      dispatch(
        actions.receiveFullSwipeUpLinksFailed({
          fallbackUrls: urls,
          fetchFullLinkError:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        }),
      );
    }
  },
);

export const removeAllClipsCustomizations = createThunk(
  () => (dispatch, getState) => {
    const storyClips = getCurrentStoryClips(getState());
    storyClips.forEach((materializedClip, index) => {
      dispatch(removeClipCustomizationsAndShowroomData({ clipIndex: index }));
    });
  },
);

export default InstagramStoryGroupPostDetailsReducer;
